
import Summary from "./summary.vue";
import { Options, Vue } from 'vue-class-component';
import { SttHangingController } from '@/app/ui/controllers/SttHangingController';
import { HangingOutgoingSummary, HangingOutgoingRequest, DownloadHangingOutgoing, ConfirmHangingOutgoingRequest } from '@/domain/entities/HangingOutgoing';
import Actions from "../tab-hanging-incoming/components/actions.vue";
import CardProgress from "../tab-hanging-incoming/components/card-progress.vue";
import CardCollapsible from "../shared/card-collapsible-hanging.vue";
import { CardHangingShared } from '@/domain/entities/CardHangingShared';
import { debounce } from 'lodash';
import Filter from "../tab-hanging-incoming/components/filter.vue";
import ConfirmAction from "@/app/ui/views/dashboard/component/console/hanging/components/ConfirmAction.vue";
import CopyTemplate from "@/app/ui/views/dashboard/component/console/hanging/components/CopyTemplate.vue";
import { AccountController } from "@/app/ui/controllers/AccountController";
import { MainAppController } from '@/app/ui/controllers/MainAppController';
import { SubmitHangingConfirmationUpdateStatus } from '@/data/payload/api/HangingApiRequest';

@Options({
  components: {
    Summary,
    Filter,
    Actions,
    CardProgress,
    CardCollapsible,
    ConfirmAction,
    CopyTemplate
  }
})
class TabHangingOutgoing extends Vue {
  dataSummary = new HangingOutgoingSummary();
  isLoadingFetch = false;
  isLoadingList = false;
  hangingOutgoingList: CardHangingShared[] = [];
  filter = new HangingOutgoingRequest();
  isOpenModal = false;
  messageHtml = "";
  lastStatus = "";
  isOpenTemplate = false;
  payloadUpdateStatus = new ConfirmHangingOutgoingRequest();
  selectedCard = {
    stt: [] as string[],
    groupName: ""
  };

  mounted() {
    this.getHangingOutgoingSummary();
    this.getHangingOutgoingList(new HangingOutgoingRequest())
  }

  get isEmptyList() {
    const isEmptyFilter = Object.values(this.filter).every(value => value === "" || value == null);
    return !this.hangingOutgoingList.length && !this.isLoadingList && isEmptyFilter;
  }

  get accountData() {
    return AccountController.accountData;
  }

  async getHangingOutgoingSummary() {
    try {
      this.isLoadingFetch = true;
      const res = await SttHangingController.getHangingOutgoingSummary();
      this.dataSummary = res;
    } catch (error) {
      console.log("error: ", error);
    } finally {
      this.isLoadingFetch = false;
    }
  }

  async getHangingOutgoingList(params: HangingOutgoingRequest) {
    try {
      this.isLoadingList = true;
      const resp = await SttHangingController.getHangingOutgoing(params);
      this.hangingOutgoingList = resp;
    } catch (error) {
      console.log("error: ", error);
      this.hangingOutgoingList = [];
    } finally {
      this.isLoadingList = false;
    }
  }

  getFilter = debounce((filter: any) => {
    const params = new HangingOutgoingRequest({ existenceStatus: filter.existenceStatus, days: filter.days, search: filter.search, statusFollowUp: filter.statusFollowUp });
    this.filter = params;
    this.getHangingOutgoingList(this.filter);
  }, 250)

  downloadHangingOutgoing(stts: string[] = []) {
    SttHangingController.downloadHangingOutgoing(new DownloadHangingOutgoing(stts));
  }

  onConfirmUpdateStatus(status: string) {
    this.lastStatus = status;
    this.messageHtml = this.$t("dashboard.hanging.confirmUpdateMessage", { status })
    this.isOpenModal = true
  }

  onSuccessUpdateStatus() {
    this.getHangingOutgoingSummary();
    this.getHangingOutgoingList(this.filter);
  }

  async onClickTemplateMessage(item: CardHangingShared) {
    this.selectedCard = {
      stt: item.groups.flatMap((group) => group.stts).map((stt) => stt.sttNo),
      groupName: item.groups.flatMap((group) => group.groupName).join(",")
    }
    await this.$nextTick()
    this.isOpenTemplate = true;
  }

  async onConfirmDone(data: CardHangingShared){
    try {
      MainAppController.closeErrorMessage();
      MainAppController.showLoading();

      await SttHangingController.confirmHangingOutgoing(
        new ConfirmHangingOutgoingRequest({
          activityType: data.activityType,
          opsUsername: data.followUp.opsUsername
        })
      );

      MainAppController.setSnackbarMessage(
        `<div class="font-bold">${this.$t("dashboard.hanging.successUpdate")}</div>`
      );
      MainAppController.setSnackbarPositivity(true);
      MainAppController.setSnackbarTimeout(2000);
      MainAppController.setSnackbar(true);

      this.onSuccessUpdateStatus();
    } catch {
      MainAppController.setSnackbarMessage(
        `<div class="font-bold">${this.$t("dashboard.hanging.failUpdate")}</div>`
      );
      MainAppController.setSnackbarPositivity(false);
      MainAppController.setSnackbarTimeout(2000);
      MainAppController.setSnackbar(true);
    } finally {
      MainAppController.closeLoading();
    }
  }

  async onUpdateStatus() {
    try {
      this.isOpenModal = false;
      MainAppController.showLoading();
      const statusUppercase = this.lastStatus.toUpperCase();
      const listStt = this.hangingOutgoingList
        .flatMap(item => item.groups)
        .flatMap(group => group.stts)
        .filter(stt => stt.isChecked)
        .map(stt => stt.sttNo);

      const payload = new SubmitHangingConfirmationUpdateStatus({
        customProcessStatus: statusUppercase,
        sttNo: listStt,
        remarks: "Hanging",
        pageType: "hanging"
      })
      const resp = await SttHangingController.submitHangingConfirmationUpdateStatus(payload)
      const message = resp.totalSttFailed
        ? `<div class="text-base">${this.$t("dashboard.hanging.successUpdateStatusWithRatio", { status: this.lastStatus, success: resp.totalSttSuccess, total: resp.totalSttSuccess + resp.totalSttFailed })}</div>`
        : `<div class="text-base">${this.$t("dashboard.hanging.successUpdateStatus", { status: this.lastStatus })}</div>`;
      const color = resp.totalSttFailed ? '#FAB713' : '#36B37E';
      MainAppController.setSnackbarMessage(message);
      MainAppController.setSnackbarPositivity(true);
      MainAppController.setSnackbarTimeout(2000);
      MainAppController.setSnackbarBackgroundColor(color);
      MainAppController.setSnackbar(true);
      this.onSuccessUpdateStatus()
    } catch (err) {
      MainAppController.setSnackbarMessage(`<div class="text-base">${this.$t("dashboard.hanging.failUpdateStatus", { status: this.lastStatus })}</div>`);
      MainAppController.setSnackbarPositivity(false);
      MainAppController.setSnackbarTimeout(2000);
      MainAppController.setSnackbar(true);
    } finally {
      MainAppController.closeLoading();
    }
  }

}
export default TabHangingOutgoing;
